import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark';

type ThemeStore = {
  /** Current theme mode */
  theme: Theme;
  /** Set theme mode */
  setTheme: (theme: Theme) => void;
  /** Whether the theme was set manually by the user */
  isManuallySet: boolean;
};

const detectSystemTheme = (): Theme => (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

const applyTheme = (theme: Theme) => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
};

const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: detectSystemTheme(),
      setTheme: (theme) => set({ theme, isManuallySet: true }),
      isManuallySet: false,
    }),
    {
      name: 'dbs-theme-mode',
      onRehydrateStorage: () => (state) => {
        // Apply theme after hydration completes
        if (state) applyTheme(state.theme);
      },
    },
  ),
);

// Apply theme to DOM and listen for changes
if (typeof window !== 'undefined') {
  // Subscribe to store changes
  useThemeStore.subscribe((state) => applyTheme(state.theme));

  // Listen for system theme changes (optional but nice UX)
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    // Only auto-switch if user hasn't manually set a preference
    const { isManuallySet } = useThemeStore.getState();
    if (!isManuallySet) {
      useThemeStore.getState().setTheme(e.matches ? 'dark' : 'light');
    }
  });
}

export { type Theme, useThemeStore };
